import {isFeatureEnabled} from '@github-ui/feature-flags'
import {Filter} from '@github-ui/filter'
import type {FilterProvider, FilterQuery} from '@github-ui/filter-core'
import {useMediaQuery} from '@github-ui/use-media-query'
import {useCallback, useMemo, useState} from 'react'
import {useCategory} from '../contexts/CategoryContext'
import {useCopilotApp} from '../contexts/CopilotAppContext'
import {useFilterContext} from '../contexts/FilterContext'
import {useQuery} from '../contexts/QueryContext'
import {useSearchType} from '../contexts/SearchTypeContext'
import useActionsFilterProviders from '../hooks/use-actions-filter-providers'
import useAppsFilterProviders from '../hooks/use-apps-filter-providers'
import {getListingTypeFilterProvider} from '../utilities/filter-providers'
import {filterStringFromQuery, queryFromFilterString} from '../utilities/filters'
import styles from './marketplace-search-field.module.css'

export default function MarketplaceSearchField() {
  const {onQueryChange: onQueryChangeV2} = useFilterContext()
  const {query} = useQuery()
  const {copilotApp, setCopilotApp} = useCopilotApp()
  const {type, setType} = useSearchType()
  const {category, setCategory} = useCategory()
  const hideCopilotExtensions = isFeatureEnabled('copilot_extensions_removal_on_marketplace')
  const initialFilter = filterStringFromQuery(type, copilotApp, query, category, hideCopilotExtensions)
  const [privateQuery, setPrivateQuery] = useState<string>(initialFilter)
  const appsProviders = useAppsFilterProviders()
  const actionsProviders = useActionsFilterProviders()
  const listingTypeProvider: FilterProvider = getListingTypeFilterProvider(hideCopilotExtensions)

  const providers = useMemo(() => {
    const generalProviders = [listingTypeProvider]
    let result: FilterProvider[] = [...generalProviders]

    switch (type) {
      case 'actions':
        result = result.concat(actionsProviders)
        break
      case 'apps':
        result = result.concat(appsProviders)
        break
      default:
        break
    }
    return result
  }, [type, appsProviders, actionsProviders, listingTypeProvider])

  const setQuery = useCallback(
    (filterStr: string) => {
      const {
        type: newType,
        copilotApp: newCopilotApp,
        query: newQuery,
        category: newCategory,
      } = queryFromFilterString(filterStr, hideCopilotExtensions)
      setType(newType)
      setCopilotApp(hideCopilotExtensions ? null : newCopilotApp)
      setCategory(newCategory)
      onQueryChangeV2(newQuery || '')
    },
    [onQueryChangeV2, setType, setCopilotApp, setCategory, hideCopilotExtensions],
  )

  const handleQueryChange = useCallback(
    (value: string) => {
      setPrivateQuery(value)
    },
    [setPrivateQuery],
  )

  const handleSubmit = useCallback(
    (filterQuery: FilterQuery) => {
      setQuery(filterQuery.raw)
    },
    [setQuery],
  )

  // 768px matches Primer's --breakpoint-medium / --viewportRange-regular token (48rem).
  const isRegularViewport = useMediaQuery('(min-width: 768px)')
  const placeholder = isRegularViewport
    ? `Search for ${hideCopilotExtensions ? '' : 'Copilot extensions, '}apps and actions`
    : 'Search in GitHub Marketplace'

  return (
    <Filter
      id="marketplace-search-filter"
      data-testid="marketplace-search-filter"
      providers={providers}
      /* eslint-disable-next-line @github-ui/dotcom-primer/no-title-case -- baselined during linter rollout */
      label="Search Marketplace"
      variant="input"
      filterValue={privateQuery}
      placeholder={placeholder}
      className={`${styles['search-input']}`}
      settings={{
        disableAdvancedTextFilter: true,
        groupAndKeywordSupport: false,
      }}
      onChange={handleQueryChange}
      onSubmit={handleSubmit}
    />
  )
}
